import { translateBackend } from '@assets/i18n/config';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { SxProps } from '@mui/material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@ovComponents/2-component/accordion/accordion';
import { AssetProgressBar } from '@ovComponents/3-pattern/assetProgressBar/assetProgressBar';
import { Box, Typography, useTheme } from 'hephaestus/web';
import { useSupportColors } from '../widgets/holdings/holdings.resources';

type HoldingsItemProps = {
  width?: number;
  primaryHoldingIndex: number;
  primaryAssetClassHoldings: any;
  onClick?: (secondaryAsset: any) => void;
  allPrimaryAssetClassHoldings: any[];
  testId?: string;
  sx?: SxProps;
  options?: any;
};

export const HoldingItem = ({
  primaryHoldingIndex,
  options,
  ...props
}: HoldingsItemProps): JSX.Element => {
  const theme = useTheme();
  const supportColors = useSupportColors();

  const showAllocationPercentOnDecimalPlaces =
    options?.showAllocationTwoDecimalPlaces
      ? options?.showAllocationTwoDecimalPlaces
      : false;

  return (
    <Accordion
      data-testid={props.testId}
      variant='elevation'
      multiple
      disableGutters
      fullWidthOnMobile
      sx={{
        boxShadow: 'none !important',
        backgroundColor: theme.color.surface,
        '&:before': {
          backgroundColor: theme.color.surface,
        },
        '.MuiAccordionSummary-expandIconWrapper': {
          marginTop: '35px',
          marginRight: '15px',
        },
        '.MuiAccordionSummary-root': {
          padding: '0px',
        },
        '.MuiAccordionDetails-root': {
          padding: '8px 0px 0px',
        },
        '.MuiCollapse-root': {
          backgroundColor: theme.color.surface,
        },
        ...props.sx,
      }}
    >
      <AccordionSummary
        data-testid={`${props.testId}-summary`}
        expandIcon={<ExpandMoreIcon />}
        aria-controls='panel1a-content'
        id='panel1a-header'
      >
        <Box sx={{ width: '100%', paddingLeft: '15px' }}>
          <Box
            sx={{
              display: 'flex',
            }}
          >
            <Typography variant='titleMedium'>
              {translateBackend(
                props.primaryAssetClassHoldings.assetClass.translatedName ?? '',
              )}
            </Typography>
          </Box>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <AssetProgressBar
              sx={{ width: '100%' }}
              data={props.allPrimaryAssetClassHoldings.map((item, index) => ({
                key: `section-${item.assetClass.key}-${index}`,
                percentage: item?.percentage ?? 0,
                position: 1,
                colorCode:
                  (item.assetClass.key ===
                  props.primaryAssetClassHoldings.assetClass.key
                    ? supportColors[primaryHoldingIndex]?.active
                    : supportColors[index]?.inactive) || theme.color.primary!,
              }))}
            />
            <Typography
              variant='bodySmall'
              component='p'
              sx={{ marginLeft: '10px' }}
            >
              {showAllocationPercentOnDecimalPlaces
                ? `${(props.primaryAssetClassHoldings.percentage ?? 0).toFixed(2)}%`
                : `${Math.round(props.primaryAssetClassHoldings.percentage ?? 0)}%`}
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ width: '100%' }}>
        <Box
          sx={{
            mb: 2,
            paddingX: '15px',
          }}
        >
          <Typography variant='bodySmall'>
            {translateBackend(
              props.primaryAssetClassHoldings.assetClass
                .translatedDescription ?? '',
            )}
          </Typography>
        </Box>
        <Box sx={{ paddingX: '15px' }}>
          {(
            props?.primaryAssetClassHoldings.secondaryAssetClassHoldings ?? []
          ).map((item: any, i: number) => (
            <Box
              key={`${item.assetClass.key}-${i}`}
              onClick={() => props.onClick && props?.onClick(item)}
              style={{ width: '100%', cursor: 'pointer' }}
              sx={{
                display: 'flex',
                py: 2,
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    minHeight: 16,
                    minWidth: 16,

                    bgcolor:
                      supportColors[primaryHoldingIndex]?.active ||
                      theme.color.primary!,

                    borderRadius: 1,
                  }}
                />
                <Typography
                  variant='bodySmall'
                  component='p'
                  sx={{ fontWeight: 600, textAlign: 'left', ml: 1.8, mb: 0 }}
                >
                  {translateBackend(item.assetClass.translatedName)}
                </Typography>
              </Box>
              <Typography
                variant='bodySmall'
                component='p'
                sx={{ paddingLeft: 1 }}
              >
                {showAllocationPercentOnDecimalPlaces
                  ? `${(item?.percentage ?? 0).toFixed(2)}%`
                  : `${Math.round(item?.percentage ?? 0)}%`}
              </Typography>
            </Box>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
